import * as React from 'react';
import { Label } from '@patternfly/react-core';
import { Area, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  type ChartWindow,
  monthlySeries,
  sliceMonths,
  trajectoryVerdict,
  windowCoverage,
} from '../../../../../shared/riskregister/executiveCharts';
import type { Payload, Row } from '../../../../../shared/riskregister/types';
import { ChartWindowSelect } from './ChartWindowSelect';
import '../../executiveCharts.css';

const TRAJECTORY_COLORS = {
  onTime: 'var(--accent-blue)',
  overdue: 'var(--accent-red)',
  resolved: 'var(--accent-green)',
} as const;

function verdictLabel(direction: 'up' | 'down' | 'flat'): { color: 'green' | 'red' | 'blue'; text: string } {
  if (direction === 'up') return { color: 'red', text: 'Open risk is higher' };
  if (direction === 'down') return { color: 'green', text: 'Open risk is lower' };
  return { color: 'blue', text: 'Open risk is flat' };
}

export const RiskTrajectoryChart: React.FunctionComponent<{
  payload: Payload;
  rows: Row[];
}> = ({ payload, rows }) => {
  const [chartWindow, setChartWindow] = React.useState<ChartWindow>('365d');
  const allMonths = React.useMemo(() => monthlySeries(rows, payload), [payload, rows]);
  const series = React.useMemo(
    () => sliceMonths(allMonths, chartWindow, payload.as_of),
    [allMonths, payload.as_of, chartWindow],
  );
  const coverage = React.useMemo(
    () => windowCoverage(allMonths, chartWindow, payload.as_of),
    [allMonths, payload.as_of, chartWindow],
  );
  const verdict = React.useMemo(() => trajectoryVerdict(series), [series]);

  if (!rows.length || !series.length) {
    return <p className="risk-register__empty">No month-end history is available for this selection.</p>;
  }

  const label = verdictLabel(verdict.direction);
  const last = series[series.length - 1];
  const description = series
    .map((point) => `${point.label}: ${point.open} open, ${point.overdue} overdue, ${point.resolved} paid down`)
    .join('. ');

  return (
    <div className="risk-register__chart-panel">
      <ChartWindowSelect
        id="risk-register-trajectory-window"
        value={chartWindow}
        onChange={setChartWindow}
        coverage={coverage}
      />

      <p className="risk-register__chart-verdict">
        <Label color={label.color} isCompact>
          {label.text}
        </Label>{' '}
        <span className="risk-register__chart-figures">
          {verdict.last} open at {verdict.lastLabel} against {verdict.first} at {verdict.firstLabel}, a change of{' '}
          {verdict.delta > 0 ? '+' : ''}
          {verdict.delta}. {last.overdue} of the {last.open} open item{last.open === 1 ? ' is' : 's are'} late.
        </span>
      </p>

      <div className="risk-register__chart" role="img" aria-label={`Open risk by month end. ${description}`}>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={series} margin={{ top: 16, right: 24, left: 0, bottom: 4 }}>
            <CartesianGrid stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: 'var(--text-secondary)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
              minTickGap={16}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: 'var(--text-secondary)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="openOnTime"
              stackId="open"
              name="Open, not late"
              stroke={TRAJECTORY_COLORS.onTime}
              fill={TRAJECTORY_COLORS.onTime}
              fillOpacity={0.35}
            />
            <Area
              type="monotone"
              dataKey="overdue"
              stackId="open"
              name="Open and late"
              stroke={TRAJECTORY_COLORS.overdue}
              fill={TRAJECTORY_COLORS.overdue}
              fillOpacity={0.5}
            />
            <Line
              type="monotone"
              dataKey="resolved"
              name="Paid down in month"
              stroke={TRAJECTORY_COLORS.resolved}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p className="risk-register__chart-note">
        The two shaded bands add up to open items at each month end, so the red band is the late share. The green line
        is what was paid down during that month. Month-end figures are rebuilt from item history, not today&apos;s
        numbers relabelled.
      </p>
    </div>
  );
};
